<script setup>
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

import ChatListHeader from './ChatListHeader.vue';
import ConversationList from './ConversationList.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import {
  isConversationUnanswered,
  sortComparator,
} from '../store/modules/conversations/helpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import { useConversationStages } from 'dashboard/composables/useConversationStages';
import {
  CONVERSATION_STAGE_AND_LEGACY_LABELS,
  EMPTY_CONVERSATION_STAGE_ID,
  LEGACY_CONVERSATION_STAGE_LABEL_MAP,
  normalizeConversationStageLabel,
} from 'dashboard/constants/conversationStages';

/* eslint-disable no-use-before-define */

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { stageLabels, stageOptions } = useConversationStages();
const { uiSettings, updateUISettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const resolveAttributesModalRef = ref(null);
const DEFAULT_CHAT_LIST_WIDTH = 390;
const MIN_CHAT_LIST_WIDTH = 300;
const MAX_CHAT_LIST_WIDTH = 720;
const chatListWidth = ref(
  Number(uiSettings.value?.conversation_list_width) || DEFAULT_CHAT_LIST_WIDTH
);
const isResizingChatList = ref(false);
const resizeStartX = ref(0);
const resizeStartWidth = ref(0);
const resizeListLabel =
  'Redimensionar lista de conversas. Arraste para ajustar a largura.';

const normalizedChatListWidth = computed(() => {
  return Math.max(
    MIN_CHAT_LIST_WIDTH,
    Math.min(MAX_CHAT_LIST_WIDTH, chatListWidth.value)
  );
});

const chatListStyle = computed(() => {
  if (props.isOnExpandedLayout) {
    return undefined;
  }

  return {
    width: `${normalizedChatListWidth.value}px`,
  };
});

const clampChatListWidth = width =>
  Math.max(MIN_CHAT_LIST_WIDTH, Math.min(MAX_CHAT_LIST_WIDTH, width));

const stopResizeChatList = () => {
  if (!isResizingChatList.value) return;

  isResizingChatList.value = false;
  updateUISettings({
    conversation_list_width: normalizedChatListWidth.value,
  });
};

const resizeChatList = event => {
  if (!isResizingChatList.value) return;

  event.preventDefault?.();
  const isRTL = document?.documentElement?.dir === 'rtl';
  const delta = event.clientX - resizeStartX.value;
  const nextWidth = isRTL
    ? resizeStartWidth.value - delta
    : resizeStartWidth.value + delta;
  chatListWidth.value = clampChatListWidth(nextWidth);
};

const startResizeChatList = event => {
  if (props.isOnExpandedLayout) return;

  isResizingChatList.value = true;
  resizeStartX.value = event.clientX;
  resizeStartWidth.value = normalizedChatListWidth.value;
};

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ALL);
const activeStatus = ref(wootConstants.STATUS_TYPE.ALL);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_MESSAGE_AT_DESC);
const showAdvancedFilters = ref(false);
const activeUnifiedFilters = ref({
  conversationState: 'all',
  period: 'all',
  agentIds: [],
  stages: [],
  labels: [],
});
const exactFilterCounts = ref({ state: {}, agents: {}, labels: {} });
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const unansweredChatsList = useMapGetter('getUnansweredChats');
const participatingChatsList = useMapGetter('getParticipatingChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getRegularLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');

const getStageFilterValues = stages => {
  const values = new Set(stages);
  Object.entries(LEGACY_CONVERSATION_STAGE_LABEL_MAP).forEach(
    ([legacyLabel, stage]) => {
      if (stages.includes(stage)) values.add(legacyLabel);
    }
  );
  return [...values];
};

const stageOptionsWithEmpty = computed(() => [
  ...stageOptions.value,
  { id: EMPTY_CONVERSATION_STAGE_ID, name: EMPTY_CONVERSATION_STAGE_ID },
]);

const knownStageFilterValues = computed(
  () =>
    new Set(
      [...stageLabels.value, ...CONVERSATION_STAGE_AND_LEGACY_LABELS].map(
        normalizeConversationStageLabel
      )
    )
);

const stageExclusionFilterValues = computed(() => [
  ...new Set([
    ...stageLabels.value,
    ...CONVERSATION_STAGE_AND_LEGACY_LABELS,
    ...[...knownStageFilterValues.value],
  ]),
]);

const splitStageFilters = stages => ({
  emptyStageSelected: stages.includes(EMPTY_CONVERSATION_STAGE_ID),
  selectedStages: stages.filter(stage => stage !== EMPTY_CONVERSATION_STAGE_ID),
});

const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const ORBIX_RESTRICTED_FILTER_USER_ID = 4;
const ORBIX_HIDDEN_ASSIGNEE_ID = 3;

const agentFilterList = computed(() => {
  if (currentUser.value?.id !== ORBIX_RESTRICTED_FILTER_USER_ID) {
    return agentList.value;
  }

  return agentList.value.filter(agent => agent.id !== ORBIX_HIDDEN_ASSIGNEE_ID);
});

const sanitizeUnifiedFilters = filters => {
  if (currentUser.value?.id !== ORBIX_RESTRICTED_FILTER_USER_ID) {
    return filters;
  }

  return {
    ...filters,
    agentIds: (filters.agentIds || []).filter(
      id => Number(id) !== ORBIX_HIDDEN_ASSIGNEE_ID
    ),
  };
};

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const assigneeTabItems = computed(() => {
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count: conversationStats.value[countKey] || 0,
  }));
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.EVERYTHING
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  const count = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  )?.count;
  return count;
});

const isEverythingTab = computed(() => {
  return activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.EVERYTHING;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const conversationFilters = computed(() => {
  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType: activeAssigneeTab.value,
    status: isEverythingTab.value
      ? wootConstants.STATUS_TYPE.ALL
      : activeStatus.value,
    sortBy: wootConstants.SORT_BY_TYPE.LAST_MESSAGE_AT_DESC,
    page: conversationListPagination.value,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
    hideResolvedStage: true,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION) {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (
    props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
  ) {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.UNATTENDED) {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

function filterByAssigneeTab(conversations) {
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return conversations.filter(
      c => c.meta?.assignee?.id === currentUser.value?.id
    );
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return conversations.filter(c => !c.meta?.assignee);
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNANSWERED) {
    return conversations.filter(isConversationUnanswered);
  }
  return [...conversations];
}

const conversationList = computed(() => {
  return baseConversationList.value
    .filter(conversation => {
      const {
        conversationState,
        period,
        agentIds,
        stages,
        labels: selectedLabels,
      } = activeUnifiedFilters.value;
      const assigneeId = conversation.meta?.assignee?.id;
      const conversationLabels = conversation.labels || [];

      const isUnanswered = isConversationUnanswered(conversation);

      if (conversationState === 'answered' && isUnanswered) {
        return false;
      }

      if (conversationState === 'unanswered' && !isUnanswered) {
        return false;
      }

      if (period === 'inside_24h' && conversation.can_reply !== true) {
        return false;
      }

      if (period === 'outside_24h' && conversation.can_reply !== false) {
        return false;
      }

      if (agentIds.length && !agentIds.includes(assigneeId)) {
        return false;
      }

      if (
        selectedLabels.length &&
        !selectedLabels.some(label => conversationLabels.includes(label))
      ) {
        return false;
      }

      const normalizedConversationLabels = conversationLabels.map(label =>
        normalizeConversationStageLabel(label)
      );
      const { emptyStageSelected, selectedStages } = splitStageFilters(stages);
      const hasSelectedStage =
        selectedStages.length &&
        selectedStages.some(stage =>
          normalizedConversationLabels.includes(stage)
        );
      const hasAnyStage = normalizedConversationLabels.some(label =>
        knownStageFilterValues.value.has(label)
      );

      if (stages.length) {
        return Boolean(
          hasSelectedStage || (emptyStageSelected && !hasAnyStage)
        );
      }

      if (normalizedConversationLabels.includes('resolvido')) {
        return false;
      }

      return true;
    })
    .sort((a, b) =>
      sortComparator(a, b, wootConstants.SORT_BY_TYPE.LAST_MESSAGE_AT_DESC)
    );
});

const baseConversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
    ) {
      localConversationList = filterByAssigneeTab(
        participatingChatsList.value(filters)
      );
    } else if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unanswered') {
      localConversationList = [...unansweredChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  return localConversationList;
});

const hasActiveUnifiedFilters = computed(() => {
  const {
    conversationState,
    period,
    agentIds,
    stages,
    labels: selectedLabels,
  } = activeUnifiedFilters.value;
  return (
    conversationState !== 'all' ||
    period !== 'all' ||
    agentIds.length > 0 ||
    stages.length > 0 ||
    selectedLabels.length > 0
  );
});

const filterCounts = computed(() => {
  const exactAllCount = Number(
    exactFilterCounts.value.state?.all ?? conversationStats.value.allCount ?? 0
  );
  const exactUnansweredCount = Number(
    exactFilterCounts.value.state?.unanswered ??
      conversationStats.value.unansweredCount ??
      0
  );
  const stateCounts = {
    all: exactAllCount,
    answered: Number(
      exactFilterCounts.value.state?.answered ??
        Math.max(exactAllCount - exactUnansweredCount, 0)
    ),
    unanswered: exactUnansweredCount,
  };
  const agents = exactFilterCounts.value.agents || {};
  const period = baseConversationList.value.reduce(
    (acc, conversation) => {
      if (conversation.can_reply === true) {
        acc.inside_24h += 1;
      }
      if (conversation.can_reply === false) {
        acc.outside_24h += 1;
      }
      return acc;
    },
    {
      all: baseConversationList.value.length,
      inside_24h: 0,
      outside_24h: 0,
    }
  );
  const stages = {};
  const labelCounts = exactFilterCounts.value.labels || {};

  Object.entries(labelCounts).forEach(([label, count]) => {
    if (label === EMPTY_CONVERSATION_STAGE_ID) {
      stages[EMPTY_CONVERSATION_STAGE_ID] = Number(count || 0);
      return;
    }

    const rawLabel = String(label || '')
      .trim()
      .toLowerCase();
    const normalizedLabel = normalizeConversationStageLabel(label);
    const stage =
      LEGACY_CONVERSATION_STAGE_LABEL_MAP[rawLabel] ||
      LEGACY_CONVERSATION_STAGE_LABEL_MAP[normalizedLabel] ||
      normalizedLabel;
    stages[stage] = (stages[stage] || 0) + Number(count || 0);
  });

  const regularLabels = labels.value.reduce((counts, label) => {
    counts[label.title] = Number(labelCounts[label.title] || 0);
    return counts;
  }, {});

  return { state: stateCounts, period, agents, stages, labels: regularLabels };
});

const showEndOfListMessage = computed(() => {
  return !!(
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  activeStatus.value = wootConstants.STATUS_TYPE.ALL;
  activeSortBy.value = wootConstants.SORT_BY_TYPE.LAST_MESSAGE_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function buildUnifiedAdvancedFilters(filters) {
  const payload = [];

  if (filters.agentIds.length) {
    payload.push({
      attributeKey: 'assignee_id',
      attributeModel: 'standard',
      customAttributeType: '',
      filterOperator: 'equal_to',
      queryOperator: 'and',
      values: filters.agentIds,
    });
  }

  if (filters.labels.length) {
    payload.push({
      attributeKey: 'labels',
      attributeModel: 'standard',
      customAttributeType: '',
      filterOperator: 'equal_to',
      queryOperator: 'and',
      values: filters.labels,
    });
  }

  const { emptyStageSelected, selectedStages } = splitStageFilters(
    filters.stages
  );

  if (selectedStages.length) {
    payload.push({
      attributeKey: 'labels',
      attributeModel: 'standard',
      customAttributeType: '',
      filterOperator: 'equal_to',
      queryOperator: emptyStageSelected ? 'or' : 'and',
      values: getStageFilterValues(selectedStages),
    });
  }

  if (emptyStageSelected) {
    payload.push({
      attributeKey: 'labels',
      attributeModel: 'standard',
      customAttributeType: '',
      filterOperator: 'not_equal_to',
      queryOperator: 'and',
      values: stageExclusionFilterValues.value,
    });
  }

  return payload;
}

function onUnifiedFilterChange(filters) {
  filters = sanitizeUnifiedFilters(filters);
  activeUnifiedFilters.value = filters;
  resetBulkActions();
  emitter.emit('clearSearchInput');

  const advancedFilters = buildUnifiedAdvancedFilters(filters);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');

  if (advancedFilters.length) {
    store.dispatch('setConversationFilters', useSnakeCase(advancedFilters));
    fetchFilteredConversations(advancedFilters);
    return;
  }

  store.dispatch('clearConversationFilters');
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

async function fetchExactFilterCounts() {
  try {
    const {
      data: { payload },
    } = await ConversationApi.counts(conversationFilters.value);
    exactFilterCounts.value = payload || { state: {}, agents: {}, labels: {} };
  } catch (error) {
    exactFilterCounts.value = { state: {}, agents: {}, labels: {} };
  }
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentFilterList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  fetchExactFilterCounts();
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  activeUnifiedFilters.value = {
    conversationState: 'all',
    period: 'all',
    agentIds: [],
    stages: [],
    labels: [],
  };
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

function updateAssigneeTab(selectedTab) {
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    if (!currentPage.value) {
      fetchConversations();
    }
  }
}

function onBasicFilterChange(_value, type) {
  if (type === 'status') {
    activeStatus.value = _value;
  } else {
    activeSortBy.value = wootConstants.SORT_BY_TYPE.LAST_MESSAGE_AT_DESC;
  }
  resetAndFetchData();
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.MENTION;
  } else if (isOnParticipatingView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.PARTICIPATING;
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.UNATTENDED;
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
});

onMounted(() => {
  window.addEventListener('pointermove', resizeChatList);
  window.addEventListener('pointerup', stopResizeChatList);
  store.dispatch('setChatListFilters', conversationFilters.value);
  if (!labels.value.length) {
    store.dispatch('labels/get');
  }
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('pointermove', resizeChatList);
  window.removeEventListener('pointerup', stopResizeChatList);
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => props.conversationInbox),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
    fetchExactFilterCounts();
  }
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 conversations-list-wrap bg-n-surface-1 relative"
    :style="chatListStyle"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : '',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !conversationList.length"
      :active-unified-filters="activeUnifiedFilters"
      :agent-list="agentFilterList"
      :stage-options="stageOptionsWithEmpty"
      :label-options="labels"
      :filter-counts="filterCounts"
      :has-active-unified-filters="hasActiveUnifiedFilters"
      :unified-result-count="conversationList.length"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @unified-filter-change="onUnifiedFilterChange"
    />
    <button
      v-if="!isOnExpandedLayout"
      type="button"
      class="absolute top-2 h-10 w-2 ltr:-right-1 rtl:-left-1 z-30 cursor-col-resize rounded-full bg-transparent hover:bg-n-blue-8/20 active:bg-n-blue-8/30"
      :class="{ 'bg-n-blue-8/30': isResizingChatList }"
      :aria-label="resizeListLabel"
      :title="resizeListLabel"
      @pointerdown.prevent="startResizeChatList"
    />

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <ChatTypeTabs
      v-if="false && !hasAppliedFiltersOrActiveFolders"
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      is-compact
      @chat-tab-change="updateAssigneeTab"
    />

    <p
      v-if="!chatListLoading && !conversationList.length"
      class="flex overflow-auto justify-center items-center p-4"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <ConversationBulkActions
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      :class="isOnExpandedLayout && 'sm:!w-[24rem] !w-full'"
      @select-all-conversations="toggleSelectAll"
    />
    <ConversationList
      :conversation-list="conversationList"
      :is-loading="chatListLoading"
      :show-end-of-list-message="showEndOfListMessage"
      :label="label"
      :team-id="teamId"
      :folders-id="foldersId"
      :conversation-type="conversationType"
      :show-assignee="showAssigneeInConversationCard"
      :is-on-expanded-layout="isOnExpandedLayout"
      @load-more="loadMoreConversations"
    />
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
